import React, { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import InputWithChecks from "../../shared/InputWithChecks";
import Container from "../../shared/Container";
import PrimaryButton from "../../shared/PrimaryButton";
import BoxBorder from "../../shared/BoxBorder";
import LoadingIndicatorButton from "../../shared/LoadingIndicatorButton";
import { addDiscount, updateDiscount } from "../../../util/admin/httpAdmin";
import SuccessMessage from "../../shared/SuccessMessage";
import { queryClient } from "../../../util/admin/httpAdmin";

// Functie om de datum om te zetten naar yyyy-MM-dd
const formatDateForInput = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
};

const DiscountWebshopFormAdmin = ({ onSuccessClose, editMode = false, previousData = {} }) => {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const discountRef = useRef(null);
  const descriptionRef = useRef(null);
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState("");

  const mutationFn = editMode ? updateDiscount : addDiscount;

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      setSuccessMessage(editMode ? "Korting succesvol bijgewerkt!" : "Korting succesvol toegevoegd!");
      queryClient.invalidateQueries(["adminDiscounts"]);
      setTimeout(() => {
        onSuccessClose();
        setTimeout(() => setSuccessMessage(""), 100);
      }, 500);
    },
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const startValid = startDateRef.current?.validateInput();
    const endValid = endDateRef.current?.validateInput();
    const discountValid = discountRef.current?.validateInput();

    if (startValid && endValid && discountValid) {
      const formData = {
        startDate: startDateRef.current.getValue(),
        endDate: endDateRef.current.getValue(),
        discount: parseFloat(discountRef.current.getValue()),
        description: descriptionRef.current?.getValue() || "Korting op de hele webshop",
        discountType: "Webshop"
      };

      if (editMode && previousData?._id) {
        formData._id = previousData._id;
      }

      mutate(formData);
    }
  };

  return (
    <section>
      <Container>
        {successMessage ? (
          <SuccessMessage title="Succes!" description={successMessage} />
        ) : (
          <BoxBorder extraClasses="mxw-800 m-a g-20">
            <h2>{editMode ? "Korting op Hele Assortiment Bewerken" : "Nieuwe Korting op Hele Assortiment"}</h2>
            <form className="g-20">
              <InputWithChecks
                ref={startDateRef}
                label="Begindatum"
                inputType="date"
                validate={(value) => {
                  if (!value) return "Begindatum is verplicht.";
                  if (new Date(value) < new Date().setHours(0, 0, 0, 0)) return "Begindatum moet vandaag of later zijn.";
                  return "";
                }}
                value={editMode ? formatDateForInput(previousData.startDate) : ""}
              />
              <InputWithChecks
                ref={endDateRef}
                label="Einddatum"
                inputType="date"
                placeholder="dd/mm/yyyy"
                validate={(value) => {
                  if (!value) return "Einddatum is verplicht.";
                  if (new Date(value) <= new Date(startDateRef.current?.getValue())) return "Einddatum moet na de begindatum liggen.";
                  return "";
                }}
                value={editMode ? formatDateForInput(previousData.endDate) : ""}
              />
              <InputWithChecks
                ref={discountRef}
                label="Korting (%)"
                inputType="number"
                validate={(value) => {
                  if (!value) return "Korting is verplicht.";
                  const num = parseFloat(value);
                  if (num <= 0 || num >= 100) return "Voer een getal tussen 0 en 100 in.";
                  return "";
                }}
                placeholder="Voer de korting in (bijv. 20%)"
                value={editMode ? previousData.discount : ""}
              />
              <InputWithChecks
                ref={descriptionRef}
                label="Beschrijving (optioneel)"
                inputType="text"
                placeholder="Bijv. 'Zomerkorting' of 'Eindejaarsuitverkoop'"
                value={editMode ? previousData.description : ""}
              />

              {isError && <div className="c-r">{error?.info?.message || "Fout bij opslaan van de korting."}</div>}

              <div>
                <PrimaryButton onClick={handleFormSubmit} disabled={isPending}>
                  {editMode ? "Korting Bijwerken" : "Korting Toevoegen"}
                  {isPending && <LoadingIndicatorButton />}
                </PrimaryButton>
              </div>
            </form>
          </BoxBorder>
        )}
      </Container>
    </section>
  );
};

export default DiscountWebshopFormAdmin; 